import { type FocusEvent, type MouseEvent, type ReactNode, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ConeContext, type ConeLoad, useCone } from "./cone";
import { type DotLook, fillWords } from "./fill";
import { type ConeVisit, FillCone } from "./FillCone";
import { FillBar } from "./FillBar";
import { LevelDot } from "./LevelDot";
import { go } from "./hooks";
import { cardPlace, HOVER_MS, LEAVE_MS, portalTarget, SCROLL_GRACE_MS, useTouch } from "./Hint";

export { cardPlace, HOVER_MS, LEAVE_MS, SCROLL_GRACE_MS };

/** What the card of one dot shows; `words` are the fill words of the row or the dot. */
export interface ConeShow {
  accountId: string;
  label?: string;
  words?: string;
  visit?: ConeVisit;
  /** The dot has no estimate ("?"), so the card says so and asks the API nothing (rule U-22). */
  empty?: boolean;
}

interface Shown extends ConeShow {
  anchor: HTMLElement;
}

/** The fill words of the card title when the caller gives none: the median, with "estimated" (rule S-8). */
export const estimatedWords = (share: number | null | undefined) => (share == null ? "No estimate" : `${fillWords(share)}, estimated`);

/** A dot with no estimate shows "No estimate yet." in its card, whatever the API could give. */
export const cardLoad = (empty: boolean | undefined, loaded: ConeLoad | null): ConeLoad | null => (empty && loaded !== null ? "none" : loaded);

/** Rule U-31: the card title links to the account page of the workspace. */
export const accountHref = (slug: string, accountId: string) => `/o/${encodeURIComponent(slug)}/accounts/${encodeURIComponent(accountId)}`;

/** The hover card of a level dot (rule U-24): the small cone of the account, over the page. */
export function ConePopover({ id, shown, onClose, onStay, onLeave, touch }: { id?: string; shown: Shown; onClose: () => void; onStay?: () => void; onLeave?: () => void; touch: boolean }) {
  const member = useContext(ConeContext);
  const loaded = useCone(shown.accountId, !shown.empty);
  const found = cardLoad(shown.empty, loaded);
  const opened = useRef(performance.now());
  const card = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const box = card.current;
    if (!box) return;
    const target = (shown.anchor.firstElementChild as HTMLElement | null) ?? shown.anchor;
    setPlace(cardPlace(target.getBoundingClientRect(), box.getBoundingClientRect(), { width: innerWidth, height: innerHeight }));
  }, [shown.anchor, found]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      onClose();
      if (touch) shown.anchor.focus?.();
    };
    const away = (e: PointerEvent) => {
      const at = e.target as Node;
      if (!card.current?.contains(at) && !shown.anchor.contains(at)) onClose();
    };
    opened.current = performance.now();
    const scroll = () => {
      if (performance.now() - opened.current < SCROLL_GRACE_MS) return;
      console.info("fill cone card: closed by a scroll");
      onClose();
    };
    document.addEventListener("keydown", key);
    if (touch) document.addEventListener("pointerdown", away);
    addEventListener("scroll", scroll, true);
    return () => {
      document.removeEventListener("keydown", key);
      document.removeEventListener("pointerdown", away);
      removeEventListener("scroll", scroll, true);
    };
  }, [shown.anchor, touch, onClose]);
  if (found === null || found === "failed") return null;
  const today = typeof found === "object" ? (found.days.find((day) => day.day === 0)?.p50 ?? null) : null;
  return createPortal(
    <div
      ref={card}
      id={id}
      className={touch ? "cone-card touch" : "cone-card"}
      role={touch ? "dialog" : "tooltip"}
      aria-label={shown.label ? `Fill estimate of ${shown.label}` : "Fill estimate"}
      data-testid="cone-card"
      data-state={typeof found === "object" ? "loaded" : found}
      style={place ? { left: place.left, top: place.top } : { visibility: "hidden" }}
      onMouseEnter={onStay}
      onMouseLeave={onLeave}
    >
      <p className="cone-card-title">
        {shown.label && (
          <strong>
            {member ? (
              <a
                href={accountHref(member.slug, shown.accountId)}
                data-testid="cone-card-link"
                onClick={(e) => {
                  console.info(`fill cone card: open the account page account=${shown.accountId}`);
                  go(e);
                  onClose();
                }}
              >
                {shown.label}
              </a>
            ) : (
              shown.label
            )}
          </strong>
        )}
        {(shown.words || typeof found === "object") && <span className="sub" data-testid="cone-card-words"> · {shown.words ?? estimatedWords(today)}</span>}
      </p>
      {found === "loading" && <p className="sub">Loading the estimate…</p>}
      {found === "none" && <p className="sub">No estimate yet.</p>}
      {typeof found === "object" && <FillCone cone={found} size="card" visit={shown.visit} />}
    </div>,
    portalTarget(),
  );
}

/** The open and close timing of one card: open after a 250 ms hover, close 150 ms after the pointer leaves the dot and the card. */
export function cardTimer<T extends { anchor: unknown; accountId: string }>(set: (next: T | null) => void) {
  let current: T | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const show = (next: T | null) => {
    clearTimeout(timer);
    if (next && current?.anchor !== next.anchor) console.info(`fill cone card: open account=${next.accountId}`);
    current = next;
    set(next);
  };
  return {
    show,
    close: () => show(null),
    hover(next: T) {
      clearTimeout(timer);
      if (current?.anchor !== next.anchor) timer = setTimeout(() => show(next), HOVER_MS);
    },
    leave() {
      clearTimeout(timer);
      if (current) timer = setTimeout(() => show(null), LEAVE_MS);
    },
    stay: () => clearTimeout(timer),
  };
}

/** The card of one anchor at a time: a hover or a keyboard focus on a desktop, a tap on a touch screen. */
export function useConeCard() {
  const touch = useTouch();
  const [shown, setShownState] = useState<Shown | null>(null);
  const id = useId();
  const [timing] = useState(() => cardTimer<Shown>(setShownState));
  useEffect(() => timing.stay, [timing]);
  const card = shown && <ConePopover id={id} shown={shown} onClose={timing.close} onStay={timing.stay} onLeave={timing.leave} touch={touch} />;
  return { touch, shown, setShown: timing.show, hover: timing.hover, leave: timing.leave, close: timing.close, card, id };
}

/** The keyboard focus of a link in the row of a dot opens the card, so the dot adds no tab stop. */
const ROW_LINK = "a[href]";

/** Any content with the cone card of its account (rule U-24); with no account or outside the workspace, the plain content. */
export function ConeAnchor({
  accountId,
  label,
  words,
  visit,
  empty,
  rowFocus = true,
  ownFocus = false,
  dot = false,
  testId = "cone-dot",
  children,
}: Omit<ConeShow, "accountId"> & {
  accountId?: string | null;
  rowFocus?: boolean;
  /** A tab stop of its own: its keyboard focus opens the card, in place of the focus of the row link. */
  ownFocus?: boolean;
  dot?: boolean;
  testId?: string;
  children: ReactNode;
}) {
  const member = useContext(ConeContext);
  const { touch, shown, setShown, hover, leave, close, card, id } = useConeCard();
  const anchor = useRef<HTMLElement | null>(null);
  const show = useRef<ConeShow | null>(null);
  show.current = member && accountId ? { accountId, label, words, visit, empty } : null;
  useEffect(() => {
    const row = anchor.current?.parentElement?.closest("tr, li");
    if (touch || !row || !rowFocus) return;
    const focus = (e: Event) => {
      const link = e.target as Element;
      if (!link.matches(ROW_LINK) || !link.matches(":focus-visible") || !show.current || !anchor.current) return;
      setShown({ ...show.current, anchor: anchor.current });
      link.setAttribute("aria-describedby", id);
    };
    const blur = (e: Event) => {
      const link = e.target as Element;
      if (!link.matches(ROW_LINK)) return;
      link.removeAttribute("aria-describedby");
      leave();
    };
    row.addEventListener("focusin", focus);
    row.addEventListener("focusout", blur);
    return () => {
      row.removeEventListener("focusin", focus);
      row.removeEventListener("focusout", blur);
    };
  }, [touch, setShown, leave, id, member, accountId, rowFocus]);
  if (!member || !accountId) return <>{children}</>;
  const focusable = ownFocus && !dot;
  const focusProps = focusable
    ? {
        tabIndex: 0,
        "aria-describedby": shown ? id : undefined,
        onFocus: (e: FocusEvent<HTMLElement>) => {
          if (anchor.current && e.currentTarget.matches(":focus-visible")) setShown({ accountId, label, words, visit, empty, anchor: anchor.current });
        },
        onBlur: leave,
      }
    : {};
  const inner = dot ? children : (
    <span className="cone-text" data-testid={focusable ? `${testId}-focus` : undefined} {...focusProps}>
      {children}
    </span>
  );
  if (touch) {
    const tap = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (shown) close();
      else if (anchor.current) setShown({ accountId, label, words, visit, empty, anchor: anchor.current });
    };
    return (
      <>
        <button
          ref={(node) => (anchor.current = node)}
          type="button"
          className={dot ? "cone-anchor cone-button" : "cone-anchor cone-button cone-button-text"}
          aria-label={label ? `Fill estimate of ${label}` : "Fill estimate"}
          aria-expanded={Boolean(shown)}
          data-testid={testId}
          onClick={tap}
        >
          {inner}
        </button>
        {card}
      </>
    );
  }
  return (
    <>
      <span
        ref={(node) => (anchor.current = node)}
        className="cone-anchor"
        data-testid={testId}
        onMouseEnter={() => anchor.current && hover({ accountId, label, words, visit, empty, anchor: anchor.current })}
        onMouseLeave={leave}
      >
        {inner}
      </span>
      {card}
    </>
  );
}

/** A level dot with the cone card of its account; with no account or outside the workspace, the plain dot. */
export function ConeDot({ accountId, label, words, visit, ...look }: DotLook & Omit<ConeShow, "accountId"> & { accountId?: string | null }) {
  return (
    <ConeAnchor accountId={accountId} label={label} words={words} visit={visit} empty={look.share == null} dot>
      <LevelDot {...look} />
    </ConeAnchor>
  );
}

/** Rule U-30: the fill bar of a table row, with the cone card of its account. With no estimate, nothing. */
export function ConeBar({ accountId, label, words, visit, share, testId = "cone-bar" }: Omit<ConeShow, "accountId" | "empty"> & { accountId?: string | null; share: number | null | undefined; testId?: string }) {
  if (share == null) return null;
  return (
    <ConeAnchor accountId={accountId} label={label} words={words} visit={visit} dot testId={testId}>
      <FillBar pct={share * 100} coloured />
    </ConeAnchor>
  );
}

/** The hover card of the level dots on a map, on a desktop only; a tap on a phone opens the map box. */
export function useMarkerCone() {
  const { touch, hover, leave, close, card } = useConeCard();
  const fine = useRef(!touch);
  fine.current = !touch;
  const watch = useCallback(
    (element: HTMLElement, accountId: string | null | undefined, label?: string, extra?: Pick<ConeShow, "words" | "visit">) => {
      if (!accountId) return;
      element.addEventListener("mouseenter", () => fine.current && hover({ accountId, label, ...extra, anchor: element }));
      element.addEventListener("mouseleave", leave);
      element.addEventListener("mousedown", close);
    },
    [hover, leave, close],
  );
  return { watch, card, close };
}
